/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import * as React from "react";
import { useSelector } from "react-redux";
import { Heading16 } from "components/shared/Heading";
import { AdobeId } from "reducers/CurrentUserReducer";
import { useProfile } from "hooks/useProfile";
import { ParticipantConnectivityIndicator } from "components/ParticipantConnectivityIndicator";
import { DebugPanelStat } from "./DebugPanelStat";
import styled from "styled-components";
import { Participant } from "types/Participant";

const ParticipantContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ParticipantRow: React.FC<{
  adobeId: AdobeId;
  participant: Participant;
}> = ({ adobeId, participant }) => {
  const { profile } = useProfile(adobeId);
  const label = profile?.displayName ?? adobeId;
  return (
    <ParticipantContainer key={adobeId}>
      <ParticipantConnectivityIndicator participant={participant} />
      <DebugPanelStat
        label={label}
        value={`Websocket: ${participant.websocketConnectionState}. WebRTC: ${participant.webRtcConnectionState}. Stream active: ${participant.streamActive}.`}
      />
    </ParticipantContainer>
  );
};

export const ParticipantsDebugSection: React.FC<{}> = () => {
  const participants = useSelector((state) => state.participants);

  return (
    <>
      <hr />
      <Heading16>Participants</Heading16>
      {Object.entries(participants).map(([adobeId, participant]) => (
        <ParticipantRow
          key={adobeId}
          adobeId={adobeId}
          participant={participant}
        />
      ))}
    </>
  );
};
